import type { World } from '../sim/types';
import { reefOf } from '../sim/data/sites';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { dateLabel, epochDayOf } from '../sim/calendar';
import { displayName, speciesOf } from '../sim/species';
import { HUMPBACK, whaleCounts } from '../sim/whales';
import { HONU, turtleCounts } from '../sim/turtles';
import { MONK_SEAL, sealCounts } from '../sim/seals';
import { mauiNuiReef, mauiNuiReefData, waterSceneData, YOUNG_SEEN_MM } from '../render/habitats/data';
import { honuSprites } from '../render/turtles';
import { sealSprites } from '../render/seals';
import { HABITAT_NAMES, type Visitor } from '../render/habitats/visitors';
import { coralVerdict, islandHealthHtml, sanctuaryThreats, sinceWords, verdictWords } from './reefHealth';
import { SPECIES_NAMES, isCountedFish, type WildlifeCard } from './wildlife';
import { escapeHtml } from './format';
import type { Place } from './visit';
import type { TagSpecies } from '../sim/tags';

/**
 * The 3D view's substitute (M288): where the page cannot draw (no WebGL, or `?render=off`), an HTML panel in the ocean's
 * place, named as the substitute it is. It tells the place and the habitat chosen and what the player may know of them
 * there, read from the world as the game's own pages and cards read it (`reefHealth.ts`'s measurements, never the
 * simulation's cover; the cards' own densities; the day's visitors the scene would draw, `visitorsOf`), and offers the
 * one thing the 3D view's taps lead to that the game keeps, a tag (`WildlifeCard.tag`, through the HUD's own card and
 * its command). It reads the world and never writes it.
 */

/** A habitat the panel offers, the exhibit's own (`exhibitOf(...).habitats`), and the whole tank. */
export interface SubstituteChoice {
  id: string;
  name: string;
  /** Its depths, m, where it is water (the scene's own `depthsOf`); the reef's are its own. */
  depthRangeM?: readonly [number, number];
}

/**
 * What the panel shows: the habitat (or `overview` for the whole tank), on the reef the island chosen or none, and the
 * hour the view stands at (the observation clock's, noon unless chosen), which side of a day's swap of visitors is there.
 */
export interface SubstituteState {
  habitat: string;
  island: ReefIsland | null;
  hour?: number;
}

/** The visual features the substitute cannot give, said once on it (the brief: disabled "with a short explanation"). */
export const SUBSTITUTE_LIMITS = 'Turning and zooming the camera, the hour of day, Watch One Day and following an animal belong to the 3D view and are off here; tagging and everything in Manage and World work as usual.';

/** A name for a visitor's kind, its species' own where it has one. */
const kindName = (kind: Visitor['kind']): string => {
  try {
    return displayName(speciesOf(kind === 'seal' ? 'monkSeal' : kind));
  } catch {
    return kind;
  }
};

/** A tag button: the card the HUD shows for it, its title and its tag, as data (`ViewSubstitute` hands it to the HUD). */
const tagButton = (card: WildlifeCard, label = 'Tag or name…'): string => `<button class="link" data-card="${escapeHtml(JSON.stringify(card))}">${escapeHtml(label)}</button>`;

/** A visitor of the day, as its scene's card offers it: a tag for a honu, a hawksbill, a seal or a tiger shark not yet tagged (`main.ts`'s visitor card's). */
function visitorRow(v: Visitor, home: boolean): string {
  const name = kindName(v.kind);
  const what = `${name}, ${v.stage === 'terminal' ? 'adult' : v.stage}, about ${v.lengthM.toFixed(1)} m`;
  if (v.tag) return `<li><strong>${escapeHtml(v.tag.name)}</strong> <span class="row-note">${escapeHtml(what)}, tagged</span></li>`;
  const taggable = home && (v.kind === 'honu' || v.kind === 'honuea' || v.kind === 'seal' || v.kind === 'tigerShark') && v.stage !== 'terminal';
  const species = v.kind === 'seal' ? 'monkSeal' : v.kind;
  const card: WildlifeCard = { title: name, rows: [['Seen', `${what}, visiting today`]], tag: { species: species as TagSpecies, stage: v.stage === 'adult' ? 'adult' : 'young', ...(v.cls !== undefined ? { cls: v.cls } : {}) } };
  return `<li>${escapeHtml(what)}${taggable ? ` ${tagButton(card)}` : ''}</li>`;
}

/**
 * The day's visitors there at the hour, as a list, or the words for none: a species' visitor that swaps with another
 * during the day (`slot`) only on its side of the swap, as the scene draws it (M288's review: both sides had been listed).
 */
const visitorsHtml = (visitors: readonly Visitor[], home: boolean, hour: number): string => {
  const here = visitors.filter((v) => !v.slot || (v.slot.after ? hour >= v.slot.hour : hour < v.slot.hour));
  return here.length > 0 ? `<ul class="rows">${here.map((v) => visitorRow(v, home)).join('')}</ul>` : '<p class="note">No visitor now: they come on some days, not others.</p>';
};

/** The reef: its coral as measured (the island's, or each island's), its threats, its fish as the school cards tell them, its octopuses and the day's visitors. */
function reefHtml(world: World, place: Place, state: SubstituteState): string {
  const reef = reefOf(place);
  if (!reef || !place.coral) return '<p class="note">This site has no reef the game holds.</p>';
  const home = !place.visiting;
  // The scene's own data (its environment, read at the reef's middle depth, unused here).
  const range = mauiNuiReef(reef).depthRangeM;
  const data = mauiNuiReefData(world, place, (range[0] + range[1]) / 2);
  const f = state.island ? REEF_ISLANDS.indexOf(state.island) : -1;
  const islands = `<p class="substitute-islands"><button data-island=""${f < 0 ? ' class="on"' : ''}>All of Maui Nui</button>${REEF_ISLANDS.map((i) => `<button data-island="${i}"${state.island === i ? ' class="on"' : ''}>${escapeHtml(ISLAND_NAMES[i])}</button>`).join('')}</p>`;
  const health = state.island ? islandHealthHtml(world, state.island)
    : `<ul class="rows">${REEF_ISLANDS.map((i) => { const v = coralVerdict(world, i); return `<li class="trend-${v.trend}"><strong>${escapeHtml(ISLAND_NAMES[i])}: ${v.last ? `${v.last.value.toFixed(0)}% live coral` : 'not measured'}</strong> <span class="verdict">${escapeHtml(verdictWords(v))}</span> <span class="row-note">${escapeHtml(sinceWords(v))}</span></li>`; }).join('')}</ul>
      <p class="note">Threats now: ${escapeHtml(sanctuaryThreats(world))}</p>`;
  // The reef fish as the school cards tell them: an island's adults a hectare of its own reef, or Maui Nui's on average.
  // A tag where the scene would draw one to follow (M288's review): a school of the island's adults, its young where any
  // are in the hectare, an octopus where one of the island's is.
  const fish = data ? data.fish.map((x) => {
    const perHa = f >= 0 ? data.islands[f]?.fish.find((s) => s.species === x.species)?.perHa.value ?? 0 : x.perHa;
    const name = SPECIES_NAMES[x.species];
    const drawn = f >= 0 ? (x.initial[f] ?? 0) + (x.terminal[f] ?? 0) : 0;
    const young = f >= 0 ? x.young[f] ?? 0 : 0;
    const tag = home && state.island && !isCountedFish(x.species)
      ? `${drawn > 0 ? ` ${tagButton({ title: name, rows: [['Where', `${ISLAND_NAMES[state.island]}'s reef`]], tag: { species: x.species, island: f, stage: 'adult' } })}` : ''}${young > 0 ? ` ${tagButton({ title: `${name}, young`, rows: [['Where', `${ISLAND_NAMES[state.island]}'s reef`]], tag: { species: x.species, island: f, stage: 'young', minLengthMm: YOUNG_SEEN_MM } }, 'Tag a young one…')}` : ''}`
      : '';
    return `<li><strong>${escapeHtml(name)}</strong> <span class="row-note">about ${perHa.toFixed(perHa < 10 ? 1 : 0)} adults a hectare</span>${tag}</li>`;
  }).join('') : '';
  const octopus = data && data.octopusPerHa > 0
    ? `<li><strong>${escapeHtml(SPECIES_NAMES.octopus)}</strong> <span class="row-note">about ${data.octopusPerHa.toFixed(1)} a hectare of Maui Nui's reef</span>${home && state.island && data.octopuses.some((o) => o.island === f) ? ` ${tagButton({ title: SPECIES_NAMES.octopus, rows: [['Where', `${ISLAND_NAMES[state.island]}'s reef`]], tag: { species: 'octopus', island: f, stage: 'adult' } })}` : ''}</li>`
    : '';
  return `${islands}
    <section><h4>${state.island ? `${escapeHtml(ISLAND_NAMES[state.island])}'s reef` : 'The coral as measured'}</h4>${health}</section>
    <section><h4>Reef fish${state.island ? ` on ${escapeHtml(ISLAND_NAMES[state.island])}'s reef` : ''}</h4><ul class="rows">${fish}${octopus}</ul>${home && !state.island ? '<p class="note">Choose an island to tag or name its fish.</p>' : ''}</section>
    <section><h4>Visiting today</h4>${visitorsHtml(data?.visitors ?? [], home, state.hour ?? 12)}${data?.dolphins ? '<p class="note">Spinner dolphins are resting over the reef today.</p>' : ''}</section>`;
}

/** A water habitat: its depths, the day's whales, bottomfish and visitors, and what lives there undrawn, the scene's own. */
function waterHtml(world: World, place: Place, choice: SubstituteChoice, hour: number): string {
  const range = choice.depthRangeM ?? [0, 1];
  const name = `${choice.name.toLowerCase()}, ${Math.round(range[0])} to ${Math.round(range[1])} m`;
  const data = waterSceneData(world, place, choice.id, name, range);
  const home = !place.visiting;
  const whales = data.whales && data.whales.inHawaii.value > 0
    ? `<li><strong>${escapeHtml(displayName(speciesOf('humpback')))}</strong> <span class="row-note">about ${Math.round(data.whales.inHawaii.value).toLocaleString('en-US')} in Hawaiʻi's waters now, ${Math.round(data.whales.calvesInHawaii.value).toLocaleString('en-US')} of them calves</span>${home ? ` ${tagButton({ title: displayName(speciesOf('humpback')), rows: [['Where', 'Hawaiʻi\'s waters']], tag: { species: 'humpback', stage: 'adult' } })}` : ''}</li>`
    : '';
  const bottom = data.bottomfish.filter((b) => b.perHa > 0).map((b) => `<li><strong>${escapeHtml(displayName(speciesOf(b.species)))}</strong> <span class="row-note">about ${b.perHa.toFixed(b.perHa < 10 ? 2 : 0)} a hectare of the hard bottom, ${b.forkCm.toFixed(0)} cm on average</span></li>`).join('');
  return `<p class="note">${escapeHtml(range[0].toFixed(0))} to ${escapeHtml(range[1].toFixed(0))} m deep.</p>
    ${whales || bottom ? `<section><h4>Living here</h4><ul class="rows">${whales}${bottom}</ul></section>` : ''}
    <section><h4>Visiting today</h4>${visitorsHtml(data.visitors, home, hour)}</section>
    ${data.notDrawn.length > 0 ? `<section><h4>Also here, not drawn</h4><ul class="rows">${data.notDrawn.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul></section>` : ''}`;
}

/** The whole tank: the coral as measured and the animals at home the tank's cards offer, each with its tag. */
function overviewHtml(world: World, place: Place): string {
  const day = epochDayOf(world);
  const home = !place.visiting;
  const rows: string[] = [];
  if (place.whales) {
    const c = whaleCounts(HUMPBACK, place.whales.humpback, day);
    const name = displayName(speciesOf('humpback'));
    rows.push(`<li><strong>${escapeHtml(name)}</strong> <span class="row-note">${c.inHawaii > 0 ? `about ${Math.round(c.inHawaii).toLocaleString('en-US')} in Hawaiʻi's waters now` : 'away at their feeding grounds now'}</span>${home && c.inHawaii > 0 ? ` ${tagButton({ title: name, rows: [['Where', 'Hawaiʻi\'s waters']], tag: { species: 'humpback', stage: 'adult' } })}` : ''}</li>`);
  }
  // The honu and the seals the tank draws (`honuSprites`, `sealSprites`), a tag of each stage drawn, as their cards offer.
  if (place.turtles) {
    const c = turtleCounts(HONU, place.turtles.honu, day);
    const name = displayName(speciesOf('honu'));
    const sprites = honuSprites(HONU, place.turtles.honu.home, day);
    const adult = home && sprites.some((s) => s.adult) ? ` ${tagButton({ title: name, rows: [['Where', 'At home in Maui Nui']], tag: { species: 'honu', stage: 'adult' } })}` : '';
    const young = home && sprites.some((s) => !s.adult) ? ` ${tagButton({ title: `${name}, young`, rows: [['Where', 'At home in Maui Nui']], tag: { species: 'honu', stage: 'young' } }, 'Tag a young one…')}` : '';
    rows.push(`<li><strong>${escapeHtml(name)}</strong> <span class="row-note">about ${Math.round(c.adults).toLocaleString('en-US')} adults and ${Math.round(c.young).toLocaleString('en-US')} young at home</span>${adult}${young}</li>`);
  }
  if (place.seals) {
    const name = displayName(speciesOf('monkSeal'));
    const sprites = sealSprites(MONK_SEAL, place.seals.monk);
    const adult = home && sprites.some((s) => s.stage === 'adult') ? ` ${tagButton({ title: name, rows: [['Where', 'Maui Nui\'s coasts']], tag: { species: 'monkSeal', stage: 'adult' } })}` : '';
    const young = home && sprites.some((s) => s.stage !== 'adult') ? ` ${tagButton({ title: `${name}, young`, rows: [['Where', 'Maui Nui\'s coasts']], tag: { species: 'monkSeal', stage: 'young' } }, 'Tag a young one…')}` : '';
    rows.push(`<li><strong>${escapeHtml(name)}</strong> <span class="row-note">about ${Math.round(sealCounts(MONK_SEAL, place.seals.monk, day).population).toLocaleString('en-US')} around Maui Nui</span>${adult}${young}</li>`);
  }
  const coral = reefOf(place) && place.coral
    ? `<section><h4>The coral as measured</h4><ul class="rows">${REEF_ISLANDS.map((i) => { const v = coralVerdict(world, i); return `<li class="trend-${v.trend}"><strong>${escapeHtml(ISLAND_NAMES[i])}: ${v.last ? `${v.last.value.toFixed(0)}% live coral` : 'not measured'}</strong> <span class="verdict">${escapeHtml(verdictWords(v))}</span></li>`; }).join('')}</ul><p class="note">Threats now: ${escapeHtml(sanctuaryThreats(world))}</p></section>`
    : '';
  return `${coral}${rows.length > 0 ? `<section><h4>At home here</h4><ul class="rows">${rows.join('')}</ul></section>` : ''}`;
}

/** The panel: what it stands in for, the place and the habitat, the habitats to choose among, and what is known there. */
export function substituteHtml(world: World, place: Place, choices: readonly SubstituteChoice[], state: SubstituteState, notice: string): string {
  const choice = choices.find((c) => c.id === state.habitat);
  const where = choice ? (HABITAT_NAMES[choice.id] ?? choice.name) : 'The whole tank';
  const nav = [...choices, { id: 'overview', name: 'Whole tank' }].map((c) => `<button data-habitat="${escapeHtml(c.id)}"${c.id === (choice?.id ?? 'overview') ? ' class="on"' : ''}>${escapeHtml(c.name)}</button>`).join('');
  const body = place.visiting
    ? `<p class="note">${escapeHtml(place.name)} is not one of your sanctuaries: no wildlife is simulated here yet.</p>`
    : !choice ? overviewHtml(world, place)
      : choice.id === 'reef' ? reefHtml(world, place, state)
        : waterHtml(world, place, choice, state.hour ?? 12);
  return `<div class="card substitute-card" role="region" aria-label="Substitute for the 3D view">
    <p class="substitute-label"><span class="chip tone-info">Substitute for the 3D view</span> ${escapeHtml(notice)}</p>
    <h2>${escapeHtml(where)}</h2>
    <p class="row-note">${escapeHtml(place.name)} · ${escapeHtml(dateLabel(epochDayOf(world)))}</p>
    <nav class="substitute-nav" aria-label="Habitats">${nav}</nav>
    ${body}
    <p class="note substitute-limits">${escapeHtml(SUBSTITUTE_LIMITS)}</p>
  </div>`;
}

/** What the panel asks of the game: a habitat, an island of the reef, and a tag's card shown. */
export interface SubstituteHandlers {
  habitat(id: string): void;
  island(island: ReefIsland | null): void;
  card(card: WildlifeCard): void;
}

/**
 * The panel in the page, in the ocean's place: drawn again when what it shows changes (the world, its day, a command,
 * the habitat or the island), at most once a second at speed, and never under a press (a button redrawn between its
 * press and its release takes no click).
 */
/** Each world shown its own number, so a load or a new game redraws the panel whatever its seed and day. */
const worldIds = new WeakMap<World, number>();
let worldsSeen = 0;
const worldId = (world: World): number => {
  let id = worldIds.get(world);
  if (id === undefined) worldIds.set(world, (id = ++worldsSeen));
  return id;
};

export class ViewSubstitute {
  readonly el: HTMLElement;
  private key = '';
  private insetKey = '';
  private drawnAt = -Infinity;
  private pressing = false;

  constructor(host: HTMLElement, private readonly handlers: SubstituteHandlers) {
    this.el = document.createElement('div');
    this.el.className = 'substitute';
    host.appendChild(this.el);
    this.el.addEventListener('pointerdown', () => { this.pressing = true; });
    window.addEventListener('pointerup', () => { this.pressing = false; });
    window.addEventListener('pointercancel', () => { this.pressing = false; });
    this.el.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const habitat = target.closest<HTMLElement>('[data-habitat]')?.dataset.habitat;
      if (habitat !== undefined) return this.handlers.habitat(habitat);
      const island = target.closest<HTMLElement>('[data-island]')?.dataset.island;
      if (island !== undefined) return this.handlers.island(island === '' ? null : (island as ReefIsland));
      const card = target.closest<HTMLElement>('[data-card]')?.dataset.card;
      if (card !== undefined) this.handlers.card(JSON.parse(card) as WildlifeCard);
    });
  }

  /** Draws the panel where what it shows changed since, and a second has passed or the choice itself changed. */
  show(world: World, place: Place, choices: readonly SubstituteChoice[], state: SubstituteState, notice: string, version: number, now: number): void {
    const choice = `${place.siteId}|${place.visiting}|${state.habitat}|${state.island ?? ''}|${choices.map((c) => c.id).join(',')}`;
    const key = `${choice}|${worldId(world)}|${world.day}|${version}|${Math.floor(state.hour ?? 12)}`;
    if (key === this.key || this.pressing) return;
    const chose = !this.key.startsWith(`${choice}|`);
    if (!chose && now - this.drawnAt < 1000) return;
    this.key = key;
    this.drawnAt = now;
    this.el.innerHTML = substituteHtml(world, place, choices, state, notice);
  }

  /** The panel kept in the part of the screen the HUD leaves (`Hud.insets`), set only when it moves. */
  setInsets(insets: { top: number; right: number; bottom: number; left: number }): void {
    const key = `${insets.top}|${insets.right}|${insets.bottom}|${insets.left}`;
    if (key === this.insetKey) return;
    this.insetKey = key;
    for (const side of ['top', 'right', 'bottom', 'left'] as const) this.el.style.setProperty(`--sub-${side}`, `${Math.round(insets[side])}px`);
  }

  dispose(): void {
    this.el.remove();
  }
}
